// api/grievanceService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.kpgmp.in/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const grievanceService = {
  // Get all grievances
  getAllGrievances: async () => {
    try {
      const response = await api.get('/admin/grievances');
      return {
        success: true,
        data: response.data.grievances || response.data
      };
    } catch (error) {
      console.error('Get all grievances error:', error);
      throw error;
    }
  },

  // Get grievance by ID
  getGrievanceById: async (id) => {
    try {
      const response = await api.get(`/admin/grievances/${id}`);
      return {
        success: true,
        data: response.data.grievance || response.data
      };
    } catch (error) {
      console.error('Get grievance error:', error);
      throw error;
    }
  },

  // Create new grievance
  createGrievance: async (grievanceData) => {
    try {
      const response = await api.post('/admin/grievances', grievanceData);
      return {
        success: true,
        data: response.data.grievance || response.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Create grievance error:', error);
      throw error;
    }
  },

  // Update grievance
  updateGrievance: async (id, grievanceData) => {
    try {
      const response = await api.put(`/admin/grievances/${id}`, grievanceData);
      return {
        success: true,
        data: response.data.grievance || response.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Update grievance error:', error);
      throw error;
    }
  },

  // Delete grievance
  deleteGrievance: async (id) => {
    try {
      const response = await api.delete(`/admin/grievances/${id}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Delete grievance error:', error);
      throw error;
    }
  },

  // Update grievance status
  updateGrievanceStatus: async (id, status, remarks) => {
    try {
      const response = await api.patch(`/admin/grievances/${id}/status`, {
        status,
        remarks
      });
      return {
        success: true,
        data: response.data.grievance || response.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Update grievance status error:', error);
      throw error;
    }
  },
// ⭐ Escalate grievance (tracks forwarding information)
escalateGrievance: async (id, newLevel, newPS) => {
  try {
    if (!newLevel || !newPS) {
      throw new Error('New level and PS are required for escalation');
    }

    // ✅ CORRECT ENDPOINT - Tracks forwarding automatically
    const response = await api.post(`/grievances/${id}/escalate`, {
      level: newLevel,
      current_ps: newPS
    });
    
    return {
      success: true,
      data: response.data.grievance || response.data,
      message: response.data.message || 'Grievance escalated successfully'
    };
  } catch (error) {
    console.error('Escalate grievance error:', error);
    throw error;
  }
},
  // Get grievance statistics
  getGrievanceStats: async () => {
    try {
      const response = await fetch(`${API_URL}/grievances/stats/summary`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get stats error:', error);
      throw error;
    }
  },

  // Get grievances by status
  getGrievancesByStatus: async (status) => {
    try {
      const response = await fetch(`${API_URL}/grievances/status/${status}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get grievances by status error:', error);
      throw error;
    }
  },

  // Get grievances by level
  getGrievancesByLevel: async (level) => {
    try {
      const response = await fetch(`${API_URL}/grievances/level/${level}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get grievances by level error:', error);
      throw error;
    }
  },

  // Transfer grievance to another PS
  transferGrievance: async (id, newPS, remarks) => {
    try {
      const response = await api.post(`/admin/grievances/${id}/transfer`, {
        new_ps: newPS,
        remarks
      });
      return {
        success: true,
        data: response.data.grievance || response.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Transfer grievance error:', error);
      throw error;
    }
  }
};